import React, { createContext, useMemo } from "react";
import type { FC } from "react";

import cn from "classnames";

import { BUTTON_SIZE_BY_VIBE_CONTEXT } from "./constants/sizes";
import { getChildrenRecord } from "../../utils/react";
import { ButtonsView } from "../Button";
import { ContentsView } from "../Content";

import type { VibeProps, VibeContexts } from "./types";

import classes from "./Vibe.styl";

export const VIBE_DISPLAY_NAME = "Vibe";

export const VibeContext = createContext<{ context: VibeContexts }>({
    context: "page",
});

/**
 * Компонент, который сообщает пользователю об отсутствии контента или отображает результат на пользовательский запрос.
 *
 * <a href="https://www.figma.com/design/SZCPsHnuqBIWlYSyfmq0j1/%F0%9F%A7%A9-Sheets---Modules---Wrappers?node-id=6352-82915&t=nYiTfVbnN2e0udgU-1" target="_blank">Спека</a>
 * <br><br>
 * ### Дочерние компоненты
 *
 * | Компонент | Использование |
 * |-----------|----------|
 * | `<Vibe.Illustration />` | `<Illustration />` с предустановленным size. |
 * | `<Vibe.Title />` | `<Content.Text />` с предустановленным size. |
 * | `<Vibe.Subtitle />` | `<Content.Text />` с предустановленным size. |
 * | `<Vibe.MainButton />` | `<Button />` с предустановленным view="primary" и size. |
 * | `<Vibe.SecondaryButton />` | `<Button />` с предустановленным view="secondary" и size. |
 * @constructor
 */
export const Vibe: FC<VibeProps> = (props) => {
    const {
        platform = "web",
        context = "page",
        width = "100%",
        height = "100%",
        buttonsSize,
        children,
        "data-tsid": tsid,
        customVarsStyles,
    } = props;

    const isMobile = platform === "mob";
    const isPageMob = isMobile && context === "page";

    const contextValue = useMemo(() => {
        return { context };
    }, [context]);

    const { Illustration, Subtitle, Title, MainButton, SecondaryButton } =
        useMemo(
            () => getChildrenRecord(children, VIBE_DISPLAY_NAME, {}),
            [children],
        );

    const wrapperClasses = cn(classes["wrapper"], customVarsStyles);

    const vibeClasses = cn(classes["vibe"], {
        [classes["__mobile"]]: isPageMob,
        ...(context !== "custom"
            ? { [classes[`__context-${context}`]]: true }
            : {}),
    });

    const currentButtonsSize = useMemo(() => {
        if (isPageMob) {
            return "36";
        }

        if (context !== "custom") {
            return BUTTON_SIZE_BY_VIBE_CONTEXT[context];
        }

        return buttonsSize;
    }, [buttonsSize, context, isPageMob]);

    return (
        <div
            className={wrapperClasses}
            data-tsid={tsid || VIBE_DISPLAY_NAME}
            data-uikit={VIBE_DISPLAY_NAME}
            style={{
                width,
                height,
            }}
        >
            <VibeContext.Provider value={contextValue}>
                <div className={vibeClasses}>
                    <div className={classes["illustration-container"]}>
                        {Illustration}
                    </div>

                    <ContentsView>
                        {Title}
                        {Subtitle}
                    </ContentsView>

                    <ButtonsView
                        size={currentButtonsSize}
                        fill={false}
                        customVarsStyles={classes["buttons-custom-vars"]}
                    >
                        <ButtonsView.Stack centerButtons>
                            {SecondaryButton}
                            {MainButton}
                        </ButtonsView.Stack>
                    </ButtonsView>
                </div>
            </VibeContext.Provider>
        </div>
    );
};

export default Vibe;
